import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Universal analytics loader.
 *
 * - **Google Analytics 4** — standard web analytics (traffic sources, conversion funnel).
 *   Fires a `page_view` on every client-side route change.
 * - **Microsoft Clarity** — session recordings + heatmaps so we can watch mobile
 *   shoppers and spot where they bail.
 *
 * Both are opt-in via environment variables so they don't fire on preview/dev
 * builds unless the env var is set:
 *   - `REACT_APP_GA_MEASUREMENT_ID`  → `G-XXXXXXXX` from analytics.google.com
 *   - `REACT_APP_CLARITY_PROJECT_ID` → the short ID from clarity.microsoft.com
 */
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;

export default function Analytics() {
  const location = useLocation();

  // One-time init
  useEffect(() => {
    // --- GA4 ---
    if (GA_ID && !window.__ga_loaded) {
      window.__ga_loaded = true;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      // eslint-disable-next-line prefer-rest-params
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", GA_ID, { send_page_view: false }); // handled per-route below
    }

    // --- Microsoft Clarity ---
    if (CLARITY_ID && !window.__clarity_loaded) {
      window.__clarity_loaded = true;
      // Standard async loader snippet from clarity.microsoft.com docs
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
        t = l.createElement(r); t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", CLARITY_ID);
    }
  }, []);

  // SPA route-change page_view
  useEffect(() => {
    if (GA_ID && window.gtag) {
      window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  return null;
}
